// deviceTypeImage — resolves a device type name to one of the bundled machine PHOTOS
// (src/components/monitoring/assets/*.jpg). One photo per device type of the domain
// enum (STORAGE, CMM, EDM, MACHINING_CENTER, HANDLER, POLISHING_CENTER, OPERATOR,
// IDENTIFICATION, MARKING, CLEANING_CENTER, TREATMENT_CENTER, DEBURRING_CENTER,
// PRINTER_3D), generated with Stitch in a BRIGHT, technological, brand-neutral style
// (PO 2026-07-10, replacing the previous dark/moody set) and downloaded into the repo
// (no runtime dependency on external image hosts). Matched by the exact enum name;
// an unknown type degrades to the machining-center photo instead of rendering nothing.
import React from 'react'
import machiningCenter from './assets/machiningCenter.jpg'
import storage from './assets/storage.jpg'
import handler from './assets/handler.jpg'
import identification from './assets/identification.jpg'
import cmm from './assets/cmm.jpg'
import edm from './assets/edm.jpg'
import printer3d from './assets/printer3d.jpg'
import polishing from './assets/polishing.jpg'
import operator from './assets/operator.jpg'
import marking from './assets/marking.jpg'
import cleaning from './assets/cleaning.jpg'
import treatment from './assets/treatment.jpg'
import deburring from './assets/deburring.jpg'

/** Builds a hero-photo component with the same contract the old SVG illustrations
 * had (fills its container, `title` used as the accessible alt text). */
const makePhoto = (src, fallbackAlt) => {
  const DevicePhoto = ({ title }) => (
    <img
      src={src}
      alt={title || fallbackAlt}
      style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
    />
  )
  return DevicePhoto
}

const MachiningCenterIllustration = makePhoto(machiningCenter, 'Machining center')
const StorageIllustration = makePhoto(storage, 'Storage')
const HandlerIllustration = makePhoto(handler, 'Handler')
const IdentificationIllustration = makePhoto(identification, 'Identification')
const CmmIllustration = makePhoto(cmm, 'CMM')
const EdmIllustration = makePhoto(edm, 'EDM')
const Printer3dIllustration = makePhoto(printer3d, '3D printer')
const PolishingIllustration = makePhoto(polishing, 'Polishing center')
const OperatorIllustration = makePhoto(operator, 'Operator')
const MarkingIllustration = makePhoto(marking, 'Marking')
const CleaningIllustration = makePhoto(cleaning, 'Cleaning center')
const TreatmentIllustration = makePhoto(treatment, 'Treatment center')
const DeburringIllustration = makePhoto(deburring, 'Deburring center')

// One photo per device-type enum value (03-dominio.md). Keyed by the exact
// `device.deviceType.name`; extend by adding an enum → illustration entry here.
const ILLUSTRATION_BY_TYPE = {
  STORAGE: StorageIllustration,
  CMM: CmmIllustration,
  EDM: EdmIllustration,
  MACHINING_CENTER: MachiningCenterIllustration,
  HANDLER: HandlerIllustration,
  POLISHING_CENTER: PolishingIllustration,
  OPERATOR: OperatorIllustration,
  IDENTIFICATION: IdentificationIllustration,
  MARKING: MarkingIllustration,
  CLEANING_CENTER: CleaningIllustration,
  TREATMENT_CENTER: TreatmentIllustration,
  DEBURRING_CENTER: DeburringIllustration,
  PRINTER_3D: Printer3dIllustration
}

/**
 * @param {string|undefined} deviceTypeName e.g. `device.deviceType.name`
 * @returns {React.ComponentType} a photo component for that device kind (machining
 *   center photo as the graceful fallback for an unknown type)
 */
function resolveDeviceTypeIllustration (deviceTypeName) {
  return ILLUSTRATION_BY_TYPE[(deviceTypeName ?? '').toUpperCase()] ?? MachiningCenterIllustration
}

export {
  resolveDeviceTypeIllustration,
  ILLUSTRATION_BY_TYPE,
  MachiningCenterIllustration,
  StorageIllustration,
  HandlerIllustration,
  IdentificationIllustration,
  CmmIllustration,
  EdmIllustration,
  Printer3dIllustration,
  PolishingIllustration,
  OperatorIllustration,
  MarkingIllustration,
  CleaningIllustration,
  TreatmentIllustration,
  DeburringIllustration
}
